import type {
  GenericMapDefaultView,
  GenericMapProviderOptions,
  GenericMapStyle,
} from '@/Utils/Map/types';

const MAP_STYLES_ENDPOINT = 'https://meta.et4.de/rest.ashx/config/';
const FALLBACK_DEFAULT_STYLE_ID = 'Road';

interface MetaMapStyle {
  id?: unknown;
  title?: unknown;
  vectorUrl?: unknown;
}

interface MetaMapStylesConfig {
  defaultMapStyle?: unknown;
  defaultMapView?: unknown;
  mapStyles?: unknown;
}

export interface GenericMapConfig {
  mapStyles: GenericMapStyle[];
  defaultMapView?: GenericMapDefaultView;
}

function normalizeDefaultMapView(value: unknown): GenericMapDefaultView | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const view = value as Record<string, unknown>;
  const latitude = Number(view.latitude);
  const longitude = Number(view.longitude);
  const zoom = Number(view.zoom);
  if (
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90 ||
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180 ||
    !Number.isFinite(zoom) ||
    zoom < 0
  ) {
    return undefined;
  }
  return { latitude, longitude, zoom };
}

export function normalizeMapStylesConfig(config: unknown): GenericMapStyle[] {
  if (!config || typeof config !== 'object') return [];
  const { defaultMapStyle, mapStyles } = config as MetaMapStylesConfig;
  if (!Array.isArray(mapStyles)) return [];

  const activeStyleId =
    typeof defaultMapStyle === 'string' && defaultMapStyle.trim()
      ? defaultMapStyle.trim()
      : FALLBACK_DEFAULT_STYLE_ID;

  return mapStyles.flatMap((entry: MetaMapStyle) => {
    const id = typeof entry?.id === 'string' ? entry.id.trim() : '';
    const uri = typeof entry?.vectorUrl === 'string' ? entry.vectorUrl.trim() : '';
    if (!id || !uri) return [];

    const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title.trim() : id;
    return [{ id, title, uri, active: id === activeStyleId }];
  });
}

export function normalizeMapConfig(config: unknown): GenericMapConfig {
  return {
    mapStyles: normalizeMapStylesConfig(config),
    defaultMapView:
      config && typeof config === 'object'
        ? normalizeDefaultMapView((config as MetaMapStylesConfig).defaultMapView)
        : undefined,
  };
}

export function applyMapStylesToProviderOptions(
  configuredOptions: GenericMapProviderOptions,
  mapStyles: GenericMapStyle[],
): GenericMapProviderOptions {
  const configuredStyles = configuredOptions.customStyles;
  const availableStyles = configuredStyles ?? (mapStyles.length ? mapStyles : undefined);
  const requestedStyle = availableStyles?.find(
    (style) => style.id === configuredOptions.defaultStyle,
  );
  const activeStyle = availableStyles?.find((style) => style.active);
  const initialStyle = configuredOptions.style ?? requestedStyle?.uri ?? activeStyle?.uri;

  return {
    ...configuredOptions,
    ...(availableStyles !== undefined ? { customStyles: availableStyles } : {}),
    ...(initialStyle ? { style: initialStyle } : {}),
  };
}

export function applyDefaultMapViewToProviderOptions(
  configuredOptions: GenericMapProviderOptions,
  defaultMapView: GenericMapDefaultView | null,
): GenericMapProviderOptions {
  if (
    !defaultMapView ||
    configuredOptions.center !== undefined ||
    configuredOptions.zoom !== undefined
  ) {
    return configuredOptions;
  }
  return {
    ...configuredOptions,
    center: [defaultMapView.longitude, defaultMapView.latitude],
    zoom: defaultMapView.zoom,
  };
}

export async function fetchMapConfig(experience: string): Promise<GenericMapConfig> {
  const url = new URL(MAP_STYLES_ENDPOINT);
  url.searchParams.set('experience', experience);
  url.searchParams.set('mode', 'maps');

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Map styles request failed with status ${response.status}.`);
  }

  return normalizeMapConfig(await response.json());
}
